import { motion } from 'framer-motion';
import React, { useEffect } from 'react';

import { Footer } from '@components/Footer';
import { Header } from '@components/Header';
import { SEOHead } from '@components/SEOHead';

import { Hero } from '@ui/Hero';
import { TOCProvider } from '@ui/TOCContext';
import * as Typography from '@ui/Typography';
import { WhatsAppCTA } from '@ui/WhatsAppCTA';

import { COLORS } from '@constants/colors';

import { handleInitialHashScroll } from '@utils/initialScroll';

type BreadcrumbItem = {
  label: string;
  href?: string;
};

type ArticleLayoutProps = {
  // SEO & Meta
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;

  // Hero Section
  heroTitle: string;
  heroDescription: string;
  breadcrumbItems?: BreadcrumbItem[];

  // Article Meta (optional sidebar)
  sidebar?: {
    readingTime: string;
    author?: string;
    lastUpdated?: Date;
  };

  // WhatsApp CTA (optional)
  cta?: {
    title: string;
    description: string;
    phoneNumber: string;
    message: string;
    buttonText: string;
  };

  // Content
  children: React.ReactNode;
  className?: string;
};

export const ArticleLayout: React.FC<ArticleLayoutProps> = ({
  title,
  description,
  keywords,
  canonicalUrl,
  heroTitle,
  heroDescription,
  breadcrumbItems,
  sidebar,
  cta,
  children,
  className = '',
}) => {
  const ContentWrapper = sidebar ? TOCProvider : React.Fragment;

  // Handle initial hash scroll when page loads
  useEffect(() => {
    handleInitialHashScroll();
  }, []);

  return (
    <ContentWrapper>
      <SEOHead
        title={title}
        description={description}
        keywords={keywords}
        canonicalUrl={canonicalUrl}
      />

      <div className="min-h-screen" style={{ backgroundColor: COLORS.pearl }}>
        <Header />

        <Hero
          title={heroTitle}
          description={heroDescription}
          variant="gradient"
          showBreadcrumb={!!breadcrumbItems}
          breadcrumbItems={breadcrumbItems}
        />

        <motion.section
          className="py-0 sm:py-16"
          style={{ backgroundColor: COLORS.pearl }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <div className="max-w-7xl mx-auto px-0 sm:px-6">
            {sidebar ? (
              // Layout with sidebar
              <div className="flex gap-8 lg:gap-12 min-w-0 w-full">
                {/* Sidebar with Meta Info & TOC */}
                <div className="hidden lg:block lg:w-64 xl:w-72 flex-shrink-0">
                  <div className="sticky top-24 space-y-6">
                    <Typography.ArticleMeta
                      readingTime={sidebar.readingTime}
                      author={sidebar.author}
                      lastUpdated={sidebar.lastUpdated}
                      title={heroTitle}
                    />
                    <Typography.TableOfContents />
                  </div>
                </div>

                {/* Main Article Content */}
                <div className="flex-1 max-w-4xl min-w-0 w-full">
                  <Typography.ArticleContainer className={className}>
                    {/* Mobile Meta - Show only on mobile */}
                    <div className="lg:hidden">
                      <Typography.ArticleMeta
                        readingTime={sidebar.readingTime}
                        author={sidebar.author}
                        lastUpdated={sidebar.lastUpdated}
                        title={heroTitle}
                        className="mx-0"
                      />
                    </div>

                    {children}
                  </Typography.ArticleContainer>
                </div>
              </div>
            ) : (
              // Layout without sidebar - full width
              <div className="max-w-4xl mx-auto">
                <Typography.ArticleContainer className={className}>
                  {children}
                </Typography.ArticleContainer>
              </div>
            )}
          </div>
        </motion.section>

        {cta && (
          <motion.section
            className="w-full"
            style={{ backgroundColor: COLORS.pearl }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.4 }}
          >
            <WhatsAppCTA
              title={cta.title}
              description={cta.description}
              phoneNumber={cta.phoneNumber}
              message={cta.message}
              buttonText={cta.buttonText}
              backgroundColor={COLORS.mist}
              buttonColor={COLORS.accent}
            />
          </motion.section>
        )}

        <Footer />
      </div>
    </ContentWrapper>
  );
};
